import { NotificationTypeEnum, TNotificationType } from '@acceso/design-system';
import { create } from 'zustand';

export type NotificationType = {
  id: number;
  type: 'info' | 'warning' | 'success' | 'error';
  title: string;
  message?: string;
};

type NotificationsStore = {
  notifications: TNotificationType[];
  addNotification: (notification: Omit<TNotificationType, 'id'>) => void;
  addNotificationId: (notification: TNotificationType) => void;
  addErrorNotification: (title: string, message?: string) => void;
  addSuccessNotification: (title: string, message?: string) => void;
  addInfoNotification: (title: string, message?: string) => void;
  addWarningNotification: (title: string, message?: string) => void;
  dismissNotification: (id: number) => void;
  updateNotification: (id: number, updatedNotification: Partial<TNotificationType>) => void;
};

let index = 0;

export const useNotificationStore = create<NotificationsStore>(set => ({
  notifications: [],
  addNotification: notification =>
    set(state => ({
      notifications: [...state.notifications, { id: ++index, ...notification }],
    })),
  addNotificationId: notification =>
    set(state => ({
      notifications: [...state.notifications, { ...notification }],
    })),
  addErrorNotification: (title: string, message?: string) =>
    set(state => ({
      notifications: [
        ...state.notifications,
        { id: ++index, type: NotificationTypeEnum.ERROR, title, message },
      ],
    })),
  addSuccessNotification: (title: string, message?: string) =>
    set(state => ({
      notifications: [
        ...state.notifications,
        { id: ++index, type: NotificationTypeEnum.SUCCESS, title, message },
      ],
    })),
  addInfoNotification: (title, message) =>
    set(state => ({
      notifications: [
        ...state.notifications,
        { id: ++index, type: NotificationTypeEnum.INFO, title, message },
      ],
    })),
  addWarningNotification: (title, message) =>
    set(state => ({
      notifications: [
        ...state.notifications,
        { id: ++index, type: NotificationTypeEnum.WARNING, title, message },
      ],
    })),
  dismissNotification: id =>
    set(state => ({
      notifications: state.notifications.filter(notification => notification.id !== id),
    })),
  updateNotification: (id, updatedNotification) =>
    set(state => {
      const index = state.notifications.findIndex(notification => notification.id === id);
      if (index === -1) return { ...state };

      const updatedNotifications = [...state.notifications];
      updatedNotifications[index] = {
        ...updatedNotifications[index],
        ...updatedNotification,
      };

      return { notifications: updatedNotifications };
    }),
}));
